import { approvedSignatoryAsset } from './invoiceTemplate.config';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { cancelledWatermarkHeight, cancelledWatermarkSvgPath } from './cancelledWatermark';
import type { InvoiceResponse } from '../../api/types';
import { formatIstDate } from '../payments/paymentDates';
import { amountInWords } from './amountInWords';
import { invoiceMoney } from './invoiceCopyText';
import { pickTemplateVersion } from './pickTemplateVersion';
import { formatGstState } from './gstStates';
import {
  consignmentHead,
  consignmentRow,
  consignmentWidths,
  invoiceDisclosures,
} from './invoiceLayout';
import './print.css';
export function InvoiceDocument({ invoice }: { invoice: InvoiceResponse }) {
  const { t } = useTranslation();
  const s = invoice.snapshot,
    exempt = s.taxTreatment === 'EXEMPT',
    tpl = pickTemplateVersion(s.invoiceDate);
  useEffect(() => {
    // Page margin boxes inherit from the document root. Restore prior values
    // when leaving this invoice so settlement and other callers stay unchanged.
    const root = document.documentElement.style;
    const values = {
      '--dnm-invoice-footer': JSON.stringify(tpl.footerNote),
      '--dnm-invoice-number': JSON.stringify(
        `${invoice.invoiceNumber}${invoice.status === 'CANCELLED' ? ' · CANCELLED' : ''}`,
      ),
    };
    const previous = Object.keys(values).map((key) => [key, root.getPropertyValue(key)]);
    Object.entries(values).forEach(([key, value]) => root.setProperty(key, value));
    return () => previous.forEach(([key, value]) => root.setProperty(key, value));
  }, [invoice.invoiceNumber, invoice.status, tpl.footerNote]);
  return (
    <article
      className="dnm-document dnm-invoice-landscape"
      aria-label={exempt ? 'DNM bill of supply' : 'DNM tax invoice'}
    >
      {invoice.status === 'CANCELLED' && (
        <svg
          className="dnm-watermark"
          viewBox={`0 0 1000 ${cancelledWatermarkHeight}`}
          role="img"
          aria-label={t('invoices.invoiceDocument.cancelled')}
        >
          <path d={cancelledWatermarkSvgPath} fill="currentColor" />
        </svg>
      )}
      <header className="dnm-heading">
        <h1>{tpl.legalName}</h1>
        <h2>{exempt ? 'BILL OF SUPPLY' : 'TAX INVOICE'}</h2>
        <div>
          <b>{t('invoices.invoiceDocument.officeAddress')}</b> {tpl.officeAddress}
        </div>
        <div>
          <b>{t('invoices.invoiceDocument.gstinPan', { gstin: tpl.gstin, pan: tpl.pan })}</b>{' '}
          {t('invoices.invoiceDocument.state', { state: formatGstState(s.sellerStateCode) })}
        </div>
      </header>
      <div className="dnm-top">
        <img src={tpl.logo} alt={t('invoices.invoiceDocument.logoAlt')} />
        <table>
          <tbody>
            <tr>
              <th>{t('invoices.invoiceDocument.invoiceNo')}</th>
              <td>{invoice.invoiceNumber}</td>
              <th>{t('invoices.invoiceDocument.invoiceDate')}</th>
              <td>{formatIstDate(s.invoiceDate)}</td>
            </tr>
            <tr>
              <th>{t('invoices.invoiceDocument.freightAmount')}</th>
              <td>{invoiceMoney(s.tax.taxableAmount)}</td>
              <th>{t('invoices.invoiceDocument.dueDate')}</th>
              <td>{formatIstDate(s.dueDate)}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div className="dnm-trip dnm-disclosures">
        {invoiceDisclosures(invoice).map((value) => (
          <p key={value}>{value}</p>
        ))}
      </div>
      <table className="dnm-parties">
        <colgroup>
          <col style={{ width: '16%' }} />
          <col style={{ width: '25%' }} />
          <col style={{ width: '15%' }} />
          <col style={{ width: '44%' }} />
        </colgroup>
        <thead>
          <tr>
            <th colSpan={2}>{t('invoices.invoiceDocument.invoicedBy')}</th>
            <th colSpan={2}>{t('invoices.invoiceDocument.invoicedTo')}</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <th>{t('invoices.invoiceDocument.company')}</th>
            <td>{tpl.legalName}</td>
            <th>{t('invoices.invoiceDocument.company')}</th>
            <td>{s.buyer.companyName ?? 'Not provided'}</td>
          </tr>
          <tr>
            <th>{t('invoices.invoiceDocument.address')}</th>
            <td>{tpl.addressLines.join(', ')}</td>
            <th>{t('invoices.invoiceDocument.address')}</th>
            <td>{s.buyer.billingAddress ?? 'Not provided'}</td>
          </tr>
          <tr>
            <th>{t('invoices.invoiceDocument.stateCity')}</th>
            <td>{formatGstState(s.sellerStateCode)}</td>
            <th>{t('invoices.invoiceDocument.stateLabel')}</th>
            <td>{formatGstState(s.placeOfSupply)}</td>
          </tr>
          <tr>
            <th>{t('invoices.invoiceDocument.gstin')}</th>
            <td>{tpl.gstin}</td>
            <th>{t('invoices.invoiceDocument.gstin')}</th>
            <td>{s.buyer.gstin ?? 'Not provided'}</td>
          </tr>
        </tbody>
      </table>
      <table className="dnm-consignment">
        <colgroup>
          {consignmentWidths.map((width, i) => (
            <col key={i} style={{ width: `${(width / 274) * 100}%` }} />
          ))}
        </colgroup>
        <thead>
          <tr>
            {consignmentHead.map((label) => (
              <th key={label}>{label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr>
            {consignmentRow(invoice).map((value, i) => (
              <td key={i}>{value}</td>
            ))}
          </tr>
          {s.lines.map((line, i) => (
            <tr key={i}>
              <td colSpan={9}>
                {i + 1} {line.description} · {t('invoices.invoiceDocument.sac', { sac: line.sac })}
              </td>
              <td colSpan={3} className="dnm-money">
                {invoiceMoney(line.amount)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <table className="dnm-summary">
        <colgroup>
          <col style={{ width: '21%' }} />
          <col style={{ width: '55%' }} />
          <col style={{ width: '24%' }} />
        </colgroup>
        <thead>
          <tr>
            <th>{exempt ? 'Supply value' : 'Freight Amount'}</th>
            <th>{t('invoices.invoiceDocument.gst')}</th>
            <th>{t('invoices.invoiceDocument.total')}</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>{invoiceMoney(s.tax.taxableAmount)}</td>
            <td>
              {invoiceMoney(s.tax.totalTaxAmount)}
              {s.tax.mode === 'FCM' &&
                s.tax.lines.map((line) => (
                  <div key={line.name}>
                    {line.name} @ {line.rate} %: {invoiceMoney(line.amount)}
                  </div>
                ))}
            </td>
            <td>
              <strong>{invoiceMoney(s.tax.grandTotal)}</strong>
            </td>
          </tr>
          <tr>
            <th>{t('invoices.invoiceDocument.amountInWords')}</th>
            <td colSpan={2}>{amountInWords(s.tax.grandTotal)}</td>
          </tr>
        </tbody>
      </table>
      <p className={s.tax.mode === 'RCM' ? 'dnm-rcm' : 'dnm-note'}>{s.tax.note}</p>
      <div className="dnm-bottom">
        <section>
          <h3>{t('invoices.invoiceDocument.bankDetails')}</h3>
          <dl>
            <dt>{t('invoices.invoiceDocument.beneficiary')}</dt>
            <dd>{tpl.bank.accountName}</dd>
            <dt>{t('invoices.invoiceDocument.bank')}</dt>
            <dd>
              {tpl.bank.name}, {tpl.bank.branch}
            </dd>
            <dt>{t('invoices.invoiceDocument.accountNo')}</dt>
            <dd>{tpl.bank.accountNumber}</dd>
            <dt>{t('invoices.invoiceDocument.ifsc')}</dt>
            <dd>{tpl.bank.ifsc}</dd>
            <dt>{t('invoices.invoiceDocument.upi')}</dt>
            <dd>{tpl.bank.upiId}</dd>
          </dl>
        </section>
        <section>
          <h3>{t('invoices.invoiceDocument.terms')}</h3>
          {tpl.terms.map((term) => (
            <p key={term}>{term}</p>
          ))}
        </section>
        <section className="dnm-signatory">
          <h3>{tpl.signatoryLabel}</h3>
          <p>{t('invoices.invoiceDocument.forName', { name: tpl.legalName })}</p>
          {(tpl.signature ?? approvedSignatoryAsset) && (
            <img
              className="dnm-signature"
              src={tpl.signature ?? approvedSignatoryAsset}
              alt={t('invoices.invoiceDocument.signatureAlt')}
            />
          )}
          <span>{tpl.signatoryLabel}</span>
        </section>
      </div>
      <footer>{tpl.footerNote}</footer>
    </article>
  );
}
